import React from 'react';
import { Box, Grid, makeStyles, Typography, useMediaQuery, useTheme } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import CheckIcon from 'housecall-ui/dist/IconsAndImages/Icons/Check';

import { formatMoney } from '../utils/formatMoney';

import { OnlineBookingCard } from './OnlineBookingCard';

type Props = {
  title: string;
  description?: string;
  image?: string;
  price?: number;
  isActive?: boolean;
  onClick?: () => void;
};

const useStyles = makeStyles(() => ({
  imageClass: {
    display: 'block',
    maxHeight: '120px',
    width: `160px`,
    borderRadius: '3px',
  },
  descriptionWrapper: {
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'pre-wrap',
  },
  showMore: {
    marginLeft: '-5px',
  },
  columnContainer: {
    height: '100%',
  },
}));

export const ServiceCard = ({ title, description, price, image, onClick, isActive = false }: Props) => {
  const { imageClass, descriptionWrapper, showMore, columnContainer } = useStyles();

  const hasDescription = Boolean(description);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const showMobileVersion = isMobile && image && hasDescription;

  return (
    <OnlineBookingCard isActive={isActive} onClick={onClick}>
      <Box padding={2}>
        <Grid
          container
          wrap={showMobileVersion ? 'wrap' : 'nowrap'}
          direction={showMobileVersion ? 'column-reverse' : 'row'}
          justify="space-between"
          data-testid="service-card"
          spacing={1}
        >
          <Grid item xs={12}>
            <Grid container className={columnContainer} wrap="wrap" direction="column" justify="space-between">
              <Grid item>
                <Grid container wrap="nowrap" justify="space-between" alignItems="flex-start" spacing={1}>
                  <Grid item>
                    <Box display="flex" alignItems="center">
                      {isActive && (
                        <Box mr={0.5}>
                          <CheckIcon color="primary" />
                        </Box>
                      )}
                      <Typography variant="subtitle2" color={isActive ? 'primary' : 'textPrimary'}>
                        {title}
                      </Typography>
                    </Box>
                  </Grid>
                  {price ? (
                    <Grid item>
                      <Typography
                        variant="subtitle2"
                        color={isActive ? 'primary' : 'textPrimary'}
                        data-testid="service-price"
                      >
                        {formatMoney(price)}
                      </Typography>
                    </Grid>
                  ) : null}
                </Grid>
              </Grid>

              <Grid item>
                <Typography
                  variant="body2"
                  color="secondary"
                  data-testid="service-card-description"
                  className={descriptionWrapper}
                >
                  {description}
                </Typography>
              </Grid>

              <Grid item>
                <Button className={showMore} size="small" color="primary">
                  Show more
                </Button>
              </Grid>
            </Grid>
          </Grid>
          {image && (
            <Grid item>
              <img alt="service" className={imageClass} src={image} />
            </Grid>
          )}
        </Grid>
      </Box>
    </OnlineBookingCard>
  );
};
